/**
 * DiceRecordStamp — tiny non-clickable W-L stamp for INSIDE a card frame.
 * Only renders once the card has 3+ battles so fresh cards stay clean.
 * Uses the same /runit/card/{id}/battle-history endpoint, lightweight fetch.
 *
 * Designed to sit in the card frame footer next to the serial number —
 * glance-readable (8-9px font, monospace, auto-colored by record).
 */
import React, { useEffect, useState } from "react";
import { API } from "../../utils";

const MIN_FIGHTS = 3;

// Simple in-memory cache so list views don't fire N duplicate requests
const _cache = new Map();
const _inflight = new Map();

async function fetchRecord(id) {
  if (_cache.has(id)) return _cache.get(id);
  if (_inflight.has(id)) return _inflight.get(id);
  const p = (async () => {
    try {
      const res = await fetch(`${API}/runit/card/${encodeURIComponent(id)}/battle-history?limit=1`);
      if (!res.ok) return null;
      const data = await res.json();
      _cache.set(id, data);
      return data;
    } catch { return null; }
    finally { _inflight.delete(id); }
  })();
  _inflight.set(id, p);
  return p;
}

export default function DiceRecordStamp({ collectibleId }) {
  const [rec, setRec] = useState(null);

  useEffect(() => {
    if (!collectibleId) return;
    let alive = true;
    fetchRecord(collectibleId).then((data) => { if (alive) setRec(data); });
    return () => { alive = false; };
  }, [collectibleId]);

  if (!rec || !rec.total || rec.total < MIN_FIGHTS) return null;

  const w = rec.wins || 0;
  const l = rec.losses || 0;
  const tintCls = w > l
    ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/30"
    : w < l
      ? "text-rose-300 bg-rose-500/15 border-rose-500/30"
      : "text-amber-300 bg-amber-500/15 border-amber-500/30";

  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1 py-0 rounded-sm border text-[7px] font-black tabular-nums leading-none ${tintCls}`}
      style={{ fontFamily: "'JetBrains Mono', monospace" }}
      data-testid="dice-record-stamp"
      title={`${w}W-${l}L · ${rec.total} dice battles`}
    >
      <span>🎲</span>
      <span>{w}W-{l}L</span>
    </span>
  );
}
